import { Network } from '@capacitor/network';
import { App } from '@capacitor/app';

/**
 * Сервис для отслеживания состояния сети
 */
class NetworkService {
  constructor() {
    this.isOnline = navigator.onLine;
    this.listeners = [];
    this.capacitorNetworkStatus = null;
    this.setupListeners();
  }

  /**
   * Настройка слушателей событий сети
   */
  async setupListeners() {
    // Слушатель для браузерного события online/offline
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.notifyListeners(true);
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      this.notifyListeners(false);
    });

    // Проверка состояния сети при загрузке
    this.isOnline = navigator.onLine;

    // Для Capacitor приложений используем Capacitor Network API
    try {
      const status = await Network.getStatus();
      this.capacitorNetworkStatus = status;
      this.isOnline = status.connected;

      // Слушатель для изменений состояния сети в Capacitor
      Network.addListener('networkStatusChange', (status) => {
        this.capacitorNetworkStatus = status;
        this.isOnline = status.connected;
        this.notifyListeners(status.connected);
      });
    } catch (error) {
      console.warn('Capacitor Network plugin not available, using browser API:', error);
    }

    // Слушатель для возврата приложения в фокус (для проверки сети)
    try {
      App.addListener('appStateChange', async ({ isActive }) => {
        if (isActive) {
          await this.checkNetworkStatus();
        }
      });
    } catch (error) {
      console.warn('Capacitor App plugin not available:', error);
    }
  }

  /**
   * Проверка текущего состояния сети
   */
  async checkNetworkStatus() {
    try {
      const status = await Network.getStatus();
      this.capacitorNetworkStatus = status;
      this.isOnline = status.connected;
      return status.connected;
    } catch (error) {
      // Fallback на браузерный API
      this.isOnline = navigator.onLine;
      return navigator.onLine;
    }
  }

  /**
   * Получить текущее состояние сети
   */
  getStatus() {
    return {
      isOnline: this.isOnline,
      connectionType: this.capacitorNetworkStatus?.connectionType || 'unknown',
      capacitorStatus: this.capacitorNetworkStatus
    };
  }

  /**
   * Добавить слушатель изменений состояния сети
   */
  addListener(callback) {
    this.listeners.push(callback);
    // Сразу вызываем callback с текущим состоянием
    callback(this.isOnline);
  }

  /**
   * Удалить слушатель
   */
  removeListener(callback) {
    this.listeners = this.listeners.filter(listener => listener !== callback);
  }

  /**
   * Уведомить всех слушателей об изменении состояния
   */
  notifyListeners(isOnline) {
    this.listeners.forEach(listener => {
      try {
        listener(isOnline);
      } catch (error) {
        console.error('Error in network status listener:', error);
      }
    });
  }

  /**
   * Проверить, есть ли интернет
   */
  async isConnected() {
    await this.checkNetworkStatus();
    return this.isOnline;
  }

  /**
   * Ожидать подключения к интернету
   */
  async waitForConnection(timeout = 30000) {
    return new Promise((resolve, reject) => {
      if (this.isOnline) {
        resolve(true);
        return;
      }

      const timeoutId = setTimeout(() => {
        this.removeListener(checkConnection);
        reject(new Error('Timeout waiting for connection'));
      }, timeout);

      const checkConnection = (isOnline) => {
        if (isOnline) {
          clearTimeout(timeoutId);
          this.removeListener(checkConnection);
          resolve(true);
        }
      };

      this.addListener(checkConnection);
    });
  }
}

// Создаем единственный экземпляр сервиса
const networkService = new NetworkService();

export default networkService;
